// src/components/CentroInfoAdminDashboard.jsx
import React, { useEffect, useState } from "react";
import AddPlatformModal from "./AddPlatformModal";
import StatusBadge from "./StatusBadge";
import TicketsPage from "./TicketsPage"; // importar o novo componente de Tickets

// CentroInfoAdminDashboard - versão ligada ao backend (sem mocks)
// Carrega plataformas de /api/platforms.php (ou /api/status.php) e mostra badges de estado.

export default function CentroInfoAdminDashboard({ user, onLogout }) {
  // estados
  const [platforms, setPlatforms] = useState([]);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [currentUser, setCurrentUser] = useState({});

  // novo estado para controlar a vista de Tickets
  const [showTickets, setShowTickets] = useState(false);

  // carregar plataformas reais
  useEffect(() => {
    async function loadPlatforms() {
      try {
        const res = await fetch("/api/status.php");
        if (!res.ok) {
          const r2 = await fetch("/api/platforms.php");
          if (!r2.ok) throw new Error("Erro ao obter plataformas");
          const data2 = await r2.json();
          setPlatforms(normalizeArray(data2));
          return;
        }
        const data = await res.json();
        setPlatforms(normalizeArray(data));
      } catch (err) {
        console.error("Erro ao carregar plataformas:", err);
        setPlatforms([]);
      }
    }

    async function loadUsers() {
      setUsers([]);
    }

    loadPlatforms();
    loadUsers();

    // opcional: poll para atualizar status a cada X segundos (ex.: 60s)
    // const iv = setInterval(loadPlatforms, 60000);
    // return () => clearInterval(iv);
  }, []);

  // util normalizer
  function normalizeArray(arr) {
    if (!Array.isArray(arr)) return [];
    return arr.map((p) => ({
      id: p.id,
      name: p.name || "(sem nome)",
      category: p.category || "Outro",
      url: p.url || "",
      admins: Array.isArray(p.admins) ? p.admins : p.admins ? [p.admins] : [],
      open: p.open ?? true,
      status: p.status ?? "unknown",
      response_time_ms: p.response_time_ms ?? null,
      last_check: p.last_check ?? null,
      created_at: p.created_at ?? null
    }));
  }

  // Filtrar + pesquisa defensiva
  const visiblePlatforms = (platforms || [])
    .filter((p) => (filter === "all" ? true : p.category === filter))
    .filter((p) => {
      const q = (query || "").toLowerCase();
      const name = (p.name || "").toLowerCase();
      const cat = (p.category || "").toLowerCase();
      const url = (p.url || "").toLowerCase();
      return name.includes(q) || cat.includes(q) || url.includes(q);
    });

  // adicionar plataforma: depois do modal chamar onCreated (a plataforma já vem do backend)
  function handleCreated(created) {
    // Assume created é o objecto salvo (com id)
    setPlatforms((prev) => [
      {
        id: created.id,
        name: created.name,
        category: created.category,
        url: created.url,
        admins: created.admins || [],
        open: created.open ?? true,
        status: created.status ?? "unknown",
        response_time_ms: created.response_time_ms ?? null,
        created_at: created.created_at ?? new Date().toISOString()
      },
      ...prev
    ]);
    setShowAdd(false);
  }

  function togglePlatformOpen(id) {
    setPlatforms((prev) => prev.map((p) => (p.id === id ? { ...p, open: !p.open } : p)));
  }

  async function deletePlatform(id) {
    if (!window.confirm("Tem a certeza que pretende remover esta plataforma?")) return;
    try {
      const res = await fetch(`/api/platforms.php?id=${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" }
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      setPlatforms((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Erro ao remover plataforma:", err);
      alert("Erro ao remover plataforma: ver console");
    }
  }

  function openPlatform(p) {
    if (p.url?.startsWith("http")) {
      window.open(p.url, "_blank", "noopener");
    } else {
      setSelectedPlatform(p);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-72 h-screen bg-white border-r p-4 sticky top-0">
          <div className="mb-6">
            <h1 className="text-2xl font-bold">Centro de Informática</h1>
            <p className="text-sm text-slate-500">Painel de Administração</p>
          </div>

          <nav className="space-y-1">
            <button className="w-full text-left p-2 rounded hover:bg-slate-100">Dashboard</button>

            {/* substitui Gestão de Utilizadores pelo botão de Adicionar, no mesmo lugar/estilo */}
            <button className="w-full text-left p-2 rounded hover:bg-slate-100" onClick={() => setShowAdd(true)}>
              Adicionar Plataforma
            </button>

            {/* botão Tickets: mesmo lugar/estilo visual dos botões da sidebar */}
            <button
              className="w-full text-left p-2 rounded hover:bg-slate-100"
              onClick={() => setShowTickets(true)}
              title="Abrir painel de tickets"
            >
              Tickets
            </button>

            <div className="border-t my-3" />

            <div>
              <h3 className="text-xs text-slate-500 uppercase">Filtrar por categoria</h3>
              <select value={filter} onChange={(e) => setFilter(e.target.value)} className="mt-2 w-full p-2 rounded border">
                <option value="all">Todas</option>
                <option value="Académico">Académico</option>
                <option value="Suporte">Suporte</option>
                <option value="Infraestrutura">Infraestrutura</option>
                <option value="Comunicação">Comunicação</option>
              </select>
            </div>
          </nav>

          <div className="mt-8 text-xs text-slate-400">Última sincronização: agora</div>
        </aside>

        {/* Main */}
        <main className="flex-1 p-6">
          {/* Topbar */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <input placeholder="Pesquisar plataformas..." value={query} onChange={(e) => setQuery(e.target.value)} className="p-2 border rounded w-[420px]" />

              <div className="flex gap-2">
                <button onClick={() => alert("Exportar CSV (implementar API)")} className="px-3 py-2 border rounded text-sm">Exportar CSV</button>
                <button onClick={() => alert("Gerar relatório PDF (implementar)")} className="px-3 py-2 bg-indigo-600 text-white rounded text-sm">Gerar Relatório</button>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-sm">Olá, <strong>{user?.name || user?.email}</strong></div>
              <button onClick={onLogout} className="px-3 py-2 border rounded text-sm">Terminar sessão</button>
            </div>
          </div>

          {/* Se showTickets for true mostramos o TicketsPage; caso contrário mostramos o dashboard normal */}
          {showTickets ? (
            <div className="bg-slate-50 min-h-screen p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Tickets</h2>
                <div className="flex items-center gap-2">
                  <button className="px-3 py-2 border rounded text-sm" onClick={() => setShowTickets(false)}>Voltar</button>
                </div>
              </div>

              <TicketsPage user={user} />
            </div>
          ) : (
            <>
              {/* Cards */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <Card title="Plataformas" value={platforms.length} />
                <Card title="Utilizadores" value={users.length} />
                <Card title="Plataformas abertas" value={platforms.filter((p) => p.open).length} />
                <Card title="Alertas" value={0} />
              </div>

              {/* Table of platforms */}
              <section className="bg-white p-4 rounded shadow">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Plataformas & Ferramentas</h2>
                  <div className="text-sm text-slate-500">Gerir acesso e estado</div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left table-auto">
                    <thead>
                      <tr className="text-xs text-slate-500 uppercase">
                        <th className="p-2">Nome</th>
                        <th className="p-2">Categoria</th>
                        <th className="p-2">Admins</th>
                        <th className="p-2">Estado</th>
                        <th className="p-2">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visiblePlatforms.map((p) => (
                        <tr key={p.id} className="border-t">
                          <td className="p-2">{p.name}</td>
                          <td className="p-2">{p.category}</td>
                          <td className="p-2 text-sm text-slate-600">{(p.admins || []).join(", ")}</td>
                          <td className="p-2"><StatusBadge status={p.status} responseTime={p.response_time_ms} /></td>
                          <td className="p-2 flex gap-2">
                            <button onClick={() => openPlatform(p)} className="px-2 py-1 border rounded text-sm">Abrir</button>
                            <button onClick={() => togglePlatformOpen(p.id)} className="px-2 py-1 border rounded text-sm">{p.open ? "Fechar" : "Abrir"}</button>
                            <button onClick={() => alert("Editar não implementado")} className="px-2 py-1 border rounded text-sm">Editar</button>
                            <button onClick={() => deletePlatform(p.id)} className="px-2 py-1 text-red-600 text-sm">Remover</button>
                          </td>
                        </tr>
                      ))}

                      {visiblePlatforms.length === 0 && (
                        <tr>
                          <td colSpan={5} className="p-4 text-center text-slate-500">Nenhuma plataforma encontrada.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Selected platform detail */}
              {selectedPlatform && (
                <div className="mt-6 bg-white p-4 rounded shadow">
                  <h3 className="font-semibold">{selectedPlatform.name}</h3>
                  <p>Categoria: {selectedPlatform.category}</p>
                  <p>URL: {selectedPlatform.url}</p>
                  <p>Admins: {(selectedPlatform.admins || []).join(", ")}</p>

                  <button onClick={() => setSelectedPlatform(null)} className="mt-3 px-3 py-2 border rounded">Fechar</button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Add Platform Modal */}
      <AddPlatformModal open={showAdd} onClose={() => setShowAdd(false)} onCreated={handleCreated} />

      {/* User modal (simplified) */}
      {showUserModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40">
          <div className="bg-white p-6 rounded w-[720px] max-w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Gestão de Utilizadores</h3>
              <button onClick={() => setShowUserModal(false)} className="text-slate-500">Fechar</button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium">Lista</h4>
                <ul className="mt-2 space-y-2 max-h-64 overflow-auto">
                  {users.map(u => (
                    <li key={u.id} className="flex items-center justify-between p-2 border rounded">
                      <div>
                        <div className="font-medium">{u.name}</div>
                        <div className="text-xs text-slate-500">{u.email} • {u.role}</div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setCurrentUser(u)} className="px-2 py-1 border rounded text-sm">Editar</button>
                        <button onClick={() => setUsers(prev => prev.filter(x => x.id !== u.id))} className="px-2 py-1 text-red-600 text-sm">Remover</button>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-3">
                  <button onClick={() => setCurrentUser({ name: "", email: "", role: "user" })} className="px-3 py-2 bg-green-600 text-white rounded">Novo Utilizador</button>
                </div>
              </div>

              <div>
                <h4 className="font-medium">Editar / Criar</h4>
                <div className="mt-2 space-y-2">
                  <label className="block text-sm">Nome</label>
                  <input value={currentUser.name || ""} onChange={e => setCurrentUser(prev => ({ ...prev, name: e.target.value }))} className="w-full p-2 border rounded" />

                  <label className="block text-sm">Email</label>
                  <input value={currentUser.email || ""} onChange={e => setCurrentUser(prev => ({ ...prev, email: e.target.value }))} className="w-full p-2 border rounded" />

                  <label className="block text-sm">Função</label>
                  <select value={currentUser.role || "user"} onChange={e => setCurrentUser(prev => ({ ...prev, role: e.target.value }))} className="w-full p-2 border rounded">
                    <option value="user">Utilizador</option>
                    <option value="support">Suporte</option>
                    <option value="admin">Admin</option>
                  </select>

                  <div className="flex gap-2 mt-3">
                    <button onClick={() => {/* guardar user - a implementar */}} className="px-3 py-2 bg-indigo-600 text-white rounded">Guardar</button>
                    <button onClick={() => setCurrentUser({})} className="px-3 py-2 border rounded">Limpar</button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

// Card component
function Card({ title, value }) {
  return (
    <div className="bg-white p-4 rounded shadow flex flex-col">
      <div className="text-sm text-slate-500">{title}</div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
    </div>
  );
}
